import React, { useState, useRef, useEffect, useCallback } from 'react';
import { chatGameApi } from './chatGameApi';
import { readKnownPorted, isPortedOwnerSeat } from '../../utils/portedLoader';

// ⌨️ /code playground card (SLASH_CODE_DESIGN_2026_07_13.md §2).
// Three panes (HTML/CSS/JS) + RUN into a sandboxed srcdoc iframe. The
// sandbox="allow-scripts" WITHOUT allow-same-origin puts the code in an
// opaque origin: no cookies, no storage, no credentialed calls to our API.
// Nothing ever executes server-side — RUN is always local to each viewer.
// The author edits + saves for the room; everyone else is read-only + RUN.

const PANE_MAX = 8000;
const PANES = ['html', 'css', 'js'];

// The console shim baked into every run: mirrors log/warn/error (+ window
// errors) up to the card via postMessage.
const CONSOLE_SHIM = `<script>(function(){
  function send(kind, args){ try { parent.postMessage({ __sandboxLog: true, kind: kind, text: args.map(function(a){ try { return typeof a === 'object' ? JSON.stringify(a) : String(a); } catch(e){ return String(a); } }).join(' ') }, '*'); } catch(e){} }
  ['log','warn','error'].forEach(function(k){ var orig = console[k]; console[k] = function(){ send(k, [].slice.call(arguments)); if (orig) orig.apply(console, arguments); }; });
  window.onerror = function(msg, src, line){ send('error', [msg + ' (line ' + line + ')']); };
})();</scr` + `ipt>`;

// Breaking out of the srcdoc script tag is the one classic footgun — escape it.
const escapeScriptEnd = (js) => String(js || '').replace(/<\/(script)/gi, '<\\/$1');

// ⌨️🤖 E1 (09-26): a RESIDENT's program (model-written, stamped
// data.resident_code by the server) runs with the network shut. The server
// already drops any program that names a URL or a network API; this meta is
// the lock under that belt (the sandbox alone still allows uncredentialed
// requests from its opaque origin). It must be the FIRST thing in <head>.
// Human programs are composed exactly as before (no meta).
// data:/blob: media and fonts stay (a program may beep or draw text); they never leave the page.
export const RESIDENT_CODE_CSP = "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: blob:; media-src data: blob:; font-src data:; base-uri 'none'; form-action 'none'";
export const composeSrcdoc = (panes, { lockNetwork = false } = {}) => {
  const head = lockNetwork ? `<meta http-equiv="Content-Security-Policy" content="${RESIDENT_CODE_CSP}">` : '';
  return `<!doctype html><html><head>${head}<style>${panes.css}</style></head><body>${panes.html}${CONSOLE_SHIM}<script>${escapeScriptEnd(panes.js)}</scr` + `ipt></body></html>`;
};

// ✏️ ruling 31 (Nina 09-26): the owner's ask under a RESIDENT's program (≤ this many chars)
export const CODE_CHANGE_ASK_MAX = 200;
const CODE_CHANGE_COPY = {
  cap: 'No more changes today.',
  busy: 'Still working on the last one.',
  later: "That didn't go through. Try again in a minute.",
};

const CodePlaygroundCard = ({ action, gameId = null, playerId, THEME, isOwner = null }) => {
  const data = action.data || {};
  // gameId isn't threaded through the action-renderer chain — the URL always
  // has it on space pages (?gameId= or /space/:gameId).
  const derivedGameId = gameId || (() => {
    try {
      const u = new URL(window.location.href);
      return u.searchParams.get('gameId') || (u.pathname.match(/\/(?:space|s)\/([^/]+)/) || [])[1] || null;
    } catch (e) { return null; }
  })();
  const isAuthor = !!playerId && data.sender_id === playerId;
  const isResidentProgram = data.resident_code === true;
  // ✏️ Who may ask him to change it: the room's OWNER seat. An explicit prop
  // wins; otherwise the known-ported marker this device stores from the
  // status (portedLoader: owner_player_id) names the owner seat. Missing =
  // not the owner, so the door never shows to a guest; the server proves it
  // again on every ask.
  const viewerIsOwner = typeof isOwner === 'boolean'
    ? isOwner
    : (() => { try { return isPortedOwnerSeat((readKnownPorted(derivedGameId) || {}).ownerSeat, playerId); } catch (e) { return false; } })();
  const canAskChange = isResidentProgram && viewerIsOwner && !isAuthor;
  const residentName = String(data.sender_name || '').trim() || 'them';
  const accent = THEME.accent || '#7b1fa2';

  const [panes, setPanes] = useState(() => ({
    html: data.code?.html || '',
    css: data.code?.css || '',
    js: data.code?.js || '',
  }));
  const [activePane, setActivePane] = useState('html');
  const [srcdoc, setSrcdoc] = useState(null);
  const [consoleLines, setConsoleLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [assistDraft, setAssistDraft] = useState('');
  const [asking, setAsking] = useState(false);
  // ✏️ the owner's "ask him to change it": a draft, an in-flight flag, the
  // server's word when it says no ("No more changes today.")
  const [changeDraft, setChangeDraft] = useState('');
  const [changing, setChanging] = useState(false);
  const [changeNote, setChangeNote] = useState(null);
  const changedAtRef = useRef(data.code_changed_at || 0);
  const dirtyRef = useRef(false);
  const iframeRef = useRef(null);
  const lastAssistRef = useRef(data.code_assist_at || 0);

  // Remote saves (WS code_card_update patches action.data.code) flow into
  // non-author cards; the author's local edits win over echoes — EXCEPT a
  // fresh assist result (the author asked for it), which beats dirty edits.
  useEffect(() => {
    const isAssist = (data.code_assist_at || 0) > (lastAssistRef.current || 0);
    if (isAssist) {
      lastAssistRef.current = data.code_assist_at;
      dirtyRef.current = false;
      setAsking(false);
    }
    if (isAuthor && dirtyRef.current && !isAssist) return;
    setPanes({
      html: data.code?.html || '',
      css: data.code?.css || '',
      js: data.code?.js || '',
    });
  }, [data.code, data.code_assist_at, isAuthor]);

  // Console messages from OUR iframe only
  useEffect(() => {
    const onMsg = (e) => {
      if (!e.data || !e.data.__sandboxLog) return;
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow) return;
      setConsoleLines((prev) => [...prev.slice(-30), { kind: e.data.kind, text: String(e.data.text).slice(0, 500) }]);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const run = useCallback(() => {
    setConsoleLines([]);
    setSrcdoc(composeSrcdoc(panes, { lockNetwork: isResidentProgram }));
  }, [panes, isResidentProgram]);

  // ✏️ his change landed (code_changed_at moved on the row): the wait ends and,
  // since the owner asked for it, the new program runs by itself.
  useEffect(() => {
    const at = Number(data.code_changed_at) || 0;
    if (at <= (changedAtRef.current || 0)) return;
    changedAtRef.current = at;
    if (changing) {
      setChanging(false);
      setChangeNote(null);
      setConsoleLines([]);
      setSrcdoc(composeSrcdoc({ html: data.code?.html || '', css: data.code?.css || '', js: data.code?.js || '' }, { lockNetwork: isResidentProgram }));
    }
  }, [data.code_changed_at, data.code, changing, isResidentProgram]);

  // R4 (09-26): every failsafe timer remembers WHICH ask it was set for; an
  // older ask's timer never un-sticks a newer one (the server would answer
  // that press "Still working on the last one.")
  const askSeq = useRef(0);
  const askChange = async () => {
    const ask = changeDraft.replace(/\s+/g, ' ').trim().slice(0, CODE_CHANGE_ASK_MAX);
    if (!canAskChange || !ask || changing) return;
    setChanging(true);
    setChangeNote(null);
    const mine = ++askSeq.current;
    try {
      const res = await chatGameApi.requestCodeChange(derivedGameId, playerId, action.id, ask);
      const out = res && res.code_change ? res.code_change : null;
      if (out && out.ok) {
        setChangeDraft('');
        setTimeout(() => { if (askSeq.current === mine) setChanging(false); }, 90000); // failsafe unstick (the fill is ~10 s)
      } else {
        setChanging(false);
        setChangeNote((out && (out.message || CODE_CHANGE_COPY[out.reason])) || CODE_CHANGE_COPY.later);
      }
    } catch (err) {
      console.error('[CodePlayground] Change failed:', err.message);
      setChanging(false);
      setChangeNote(CODE_CHANGE_COPY.later);
    }
  };

  const save = async () => {
    if (!isAuthor || saving) return;
    setSaving(true);
    try {
      await chatGameApi.saveCodeCard(derivedGameId, playerId, action.id, panes);
      dirtyRef.current = false;
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (err) {
      console.error('[CodePlayground] Save failed:', err.message);
    }
    setSaving(false);
  };

  const btn = {
    border: 'none', borderRadius: 999, padding: '4px 12px', cursor: 'pointer',
    fontSize: '0.72em', fontWeight: 700, letterSpacing: '0.3px',
  };

  return (
    <div style={{ padding: '4px 12px 10px' }}>
      <div style={{
        borderRadius: 8, overflow: 'hidden',
        border: `1px solid ${accent}44`, background: 'rgba(12,10,20,0.85)',
      }}>
        {/* Pane tabs + actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {PANES.map((p) => (
            <button
              key={p}
              onClick={() => setActivePane(p)}
              style={{
                ...btn,
                background: activePane === p ? `${accent}33` : 'transparent',
                color: activePane === p ? '#fff' : 'rgba(255,255,255,0.55)',
                textTransform: 'uppercase',
              }}
            >
              {p}{panes[p] ? ' •' : ''}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {isAuthor && (
              <button onClick={save} disabled={saving} style={{ ...btn, background: 'rgba(255,255,255,0.12)', color: '#fff' }}>
                {saving ? '…' : savedFlash ? '✓ Saved' : '💾 Save for the room'}
              </button>
            )}
            <button onClick={run} style={{ ...btn, background: accent, color: '#fff' }}>
              ▶ Run
            </button>
          </span>
        </div>

        {/* Editor pane */}
        <textarea
          value={panes[activePane]}
          readOnly={!isAuthor}
          spellCheck={false}
          maxLength={PANE_MAX}
          onChange={(e) => {
            dirtyRef.current = true;
            const v = e.target.value;
            setPanes((prev) => ({ ...prev, [activePane]: v }));
          }}
          onKeyDown={(e) => {
            // Tab inserts two spaces instead of leaving the editor
            if (e.key === 'Tab' && isAuthor) {
              e.preventDefault();
              const t = e.target;
              const s = t.selectionStart;
              const v = panes[activePane];
              dirtyRef.current = true;
              setPanes((prev) => ({ ...prev, [activePane]: v.slice(0, s) + '  ' + v.slice(s) }));
              setTimeout(() => { t.selectionStart = t.selectionEnd = s + 2; }, 0);
            }
          }}
          placeholder={isAuthor
            ? (activePane === 'html' ? '<h1>Hello room!</h1>' : activePane === 'css' ? 'h1 { color: hotpink; }' : "console.log('hi!')")
            : 'Read-only. Hit Run to see it!'}
          style={{
            display: 'block', width: '100%', minHeight: 110, maxHeight: 220,
            boxSizing: 'border-box', resize: 'vertical', border: 'none', outline: 'none',
            background: 'transparent', color: '#d8f0d8',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.78em', lineHeight: 1.5, padding: '8px 10px',
          }}
        />

        {/* 🤖 Ask the builder (author only) — AI patches the panes; the
            result arrives over the code_card_update lane */}
        {isAuthor && (
          <div style={{ display: 'flex', gap: 6, padding: '6px 8px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <input
              value={assistDraft}
              onChange={(e) => setAssistDraft(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && assistDraft.trim() && !asking) {
                  e.preventDefault();
                  setAsking(true);
                  const mine = ++askSeq.current;
                  try {
                    await chatGameApi.requestCodeAssist(derivedGameId, playerId, action.id, assistDraft.trim());
                    setAssistDraft('');
                    setTimeout(() => { if (askSeq.current === mine) setAsking(false); }, 60000); // failsafe unstick
                  } catch (err) {
                    console.error('[CodePlayground] Assist failed:', err.message);
                    setAsking(false);
                  }
                }
              }}
              placeholder="Ask the builder: make the ball bounce"
              disabled={asking}
              style={{
                flex: 1, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 999,
                background: 'rgba(255,255,255,0.05)', color: '#fff', outline: 'none',
                padding: '4px 12px', fontSize: '0.74em',
              }}
            />
            <button
              disabled={asking || !assistDraft.trim()}
              onClick={async () => {
                if (!assistDraft.trim() || asking) return;
                setAsking(true);
                const mine = ++askSeq.current;
                try {
                  await chatGameApi.requestCodeAssist(derivedGameId, playerId, action.id, assistDraft.trim());
                  setAssistDraft('');
                  setTimeout(() => { if (askSeq.current === mine) setAsking(false); }, 60000);
                } catch (err) {
                  console.error('[CodePlayground] Assist failed:', err.message);
                  setAsking(false);
                }
              }}
              style={{ ...btn, background: asking ? 'rgba(255,255,255,0.1)' : `${accent}cc`, color: '#fff' }}
            >
              {asking ? '🤖 building…' : '🤖 Ask'}
            </button>
          </div>
        )}

        {/* ✏️ Ask him to change it (the OWNER, under a RESIDENT's program; ruling 31).
            Hidden for guests. One more fill of the same card, in his 2/day. */}
        {canAskChange && (
          <div data-testid="code-change-door" style={{ display: 'flex', gap: 6, padding: '6px 8px', borderTop: '1px solid rgba(255,255,255,0.08)', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={changeDraft}
              maxLength={CODE_CHANGE_ASK_MAX}
              onChange={(e) => setChangeDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent?.isComposing) { e.preventDefault(); askChange(); } }}
              placeholder={`Ask ${residentName} to change it: make the stars blue`}
              aria-label={`Ask ${residentName} to change it`}
              disabled={changing}
              style={{
                flex: 1, minWidth: 160, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 999,
                background: 'rgba(255,255,255,0.05)', color: '#fff', outline: 'none',
                padding: '4px 12px', fontSize: '0.74em',
              }}
            />
            <button
              disabled={changing || !changeDraft.trim()}
              onClick={askChange}
              style={{ ...btn, background: changing ? 'rgba(255,255,255,0.1)' : `${accent}cc`, color: '#fff' }}
            >
              {changing ? `${residentName} is changing it…` : '✏️ Ask'}
            </button>
            {changeNote && (
              <span role="status" style={{ flexBasis: '100%', fontSize: '0.72em', color: THEME.textMuted || '#999' }}>{changeNote}</span>
            )}
          </div>
        )}

        {/* The stage: sandboxed run output */}
        {srcdoc !== null && (
          <iframe
            ref={iframeRef}
            title="Code sandbox output"
            sandbox="allow-scripts"
            srcDoc={srcdoc}
            style={{ display: 'block', width: '100%', height: 200, border: 'none', borderTop: `1px solid ${accent}44`, background: '#fff' }}
          />
        )}

        {/* Console mirror */}
        {consoleLines.length > 0 && (
          <div style={{ maxHeight: 90, overflowY: 'auto', padding: '6px 10px', borderTop: '1px solid rgba(255,255,255,0.08)', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.7em' }}>
            {consoleLines.map((l, i) => (
              <div key={i} style={{ color: l.kind === 'error' ? '#ff8a8a' : l.kind === 'warn' ? '#ffd98a' : 'rgba(255,255,255,0.75)' }}>
                {l.kind === 'error' ? '✖ ' : l.kind === 'warn' ? '⚠ ' : '› '}{l.text}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* "Building: {topic}" belongs to a HUMAN's sandbox being built; a filled
          resident program is done (its card line already says what it is). */}
      {data.topic && !isResidentProgram && (
        <div style={{ marginTop: 6, fontSize: '0.72em', fontStyle: 'italic', color: THEME.textMuted || '#999' }}>
          Building: {data.topic}
        </div>
      )}
    </div>
  );
};

export default CodePlaygroundCard;
